import styled, { css } from 'styled-components';
import { animated } from 'react-spring';
import { darken } from 'polished';
import { ReactSVG } from 'react-svg';

/* ===== Identidade visual (alinhada ao login / Header) =====
   --primary: #ff6d3c   --ink: #2c405a / #17263b   --muted: #79869e
   --border: rgba(216, 224, 235, 0.9)
   Obs.: cores funcionais de interação (highlight do SVG e estados de
   seleção) já usam o laranja da marca e são preservadas.
=========================================================== */

export const Container = styled.div`
  display: flex;
  flex-direction: column;

  /**
   * Antes: height fixo em calc(100vh - 240px), que estourava a tela em
   * resoluções de 800px de altura e não acompanhava o header compacto.
   * Agora a altura é distribuída pelo flex do Content (Classificacao),
   * garantindo que desenho + tabela caibam sem rolagem da página.
   */
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
`;

export const Wrapper = styled.div`
  /** área do desenho: ocupa toda a altura restante */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;

  background: #fff;
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(23, 38, 59, 0.06),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  overflow: hidden;
`;

export const ZoomArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  height: 100%;
  max-height: inherit;

  .react-transform-wrapper {
    height: 100%;
    width: 100%;

    .react-transform-component {
      height: 100%;
      width: 100%;
      position: relative;
      justify-content: center;
      align-items: center;
      cursor: grab;
    }

    .react-transform-component:active {
      cursor: grabbing;
    }
  }

  /**
   * Parâmetro global 34 (REDUZ_TABELA_ITENS_HOME): com a listagem exibindo
   * apenas um item, o desenho ocupa a área liberada em tela.
   *
   * Com o layout fluido (SVG em height: 100%) esse ganho passou a ser
   * automático — a tabela reduzida encolhe e o desenho cresce. A regra
   * abaixo apenas eleva o piso mínimo do desenho nesse cenário.
   *
   * IMPORTANTE: a regra é restrita ao SVG do desenho (dentro do
   * .react-transform-component). A ZoomArea também é container do modal
   * ItemCart, da lupa e das notas — um seletor "svg" solto afetaria os ícones
   * (react-icons) desses componentes e quebraria o layout.
   * O seletor duplicado (&&) garante prioridade sobre a regra padrão do SVG.
   */
  ${({ reduzida }) =>
    reduzida &&
    css`
      && .react-transform-component svg {
        min-height: 320px;
      }
    `}
`;

export const SVG = styled(ReactSVG)`
  svg {
    /**
     * Desenho fluido: ocupa toda a área disponível da ZoomArea em vez de
     * uma altura fixa em vh (antes 40vh / máx. 390px, que desperdiçava
     * espaço em tablet). O viewBox do SVG preserva a proporção.
     */
    height: 100%;
    width: auto;
    max-width: 100%;

    /** piso para telas muito baixas, evitando desenho ilegível */
    min-height: 240px;
  }

  text {
    pointer-events: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: -moz-none;
    -o-user-select: none;
    user-select: none;

    &:hover {
      fill: #fff;
    }
  }

  /* a:hover, */
  .selecting {
    > circle {
      fill: #ff6d3c;
      stroke: #ff6d3c;
    }

    > rect {
      fill: #ff6d3c;
      stroke: #ff6d3c;
    }

    > text {
      /* fill: #fff; */
      stroke: #fff;
    }
  }

  .selecting-conjunto {
    > circle {
      fill: #fed7aa;
      stroke: #fed7aa;
    }

    > rect {
      fill: #fed7aa;
      stroke: #fed7aa;
    }

    > text {
      stroke: #9ca3af;
    }
  }

  /* =====================================================================
     REALCE DAS FORMAS DE MAPEAMENTO INTRODUZIDAS PARA ESQUEMAS ELÉTRICOS
     ---------------------------------------------------------------------
     Os links legados (círculo e retângulo com o código dentro) NÃO têm o
     atributo data-role, então nenhuma regra abaixo os alcança — o realce
     deles continua exatamente como nas regras acima.

     As novas formas são identificadas por data-role, gravado pelo editor
     (lib/const.js -> opLinkPapel) e preservado pelo svgo do backend:

       data-role="area"          retângulo sobre o contorno do componente
       data-role="chamada"       linha até a etiqueta (path)
       data-role="rotulo"        balão da etiqueta
       data-role="rotulo-texto"  texto da etiqueta

     A área é realçada por OPACIDADE, não por preenchimento sólido: com
     fill sólido o componente do desenho desapareceria embaixo do destaque.
     A especificidade do seletor de atributo (0,2,1) vence a regra
     "> rect" (0,1,1) acima, então basta redefinir o que muda.
     ===================================================================== */

  [data-role='area'] {
    transition: fill-opacity 0.15s ease, stroke-width 0.15s ease;
  }

  /* ----- item selecionado (clique na tabela ou no desenho) ----- */
  .selecting {
    > rect[data-role='area'] {
      fill: #ff6d3c;
      fill-opacity: 0.32;
      stroke: #ff6d3c;
      stroke-width: 1.4;
    }

    > path[data-role='chamada'] {
      stroke: #ff6d3c;
      stroke-width: 1.4;
    }

    /** o balão mantém o fundo claro para o texto continuar legível */
    > rect[data-role='rotulo'] {
      fill: #fff;
      fill-opacity: 1;
      stroke: #ff6d3c;
      stroke-width: 1.4;
    }

    > text[data-role='rotulo-texto'] {
      fill: #17263b;
      stroke: none;
      font-weight: bold;
    }
  }

  /* ----- item pertencente a um conjunto selecionado ----- */
  .selecting-conjunto {
    > rect[data-role='area'] {
      fill: #fed7aa;
      fill-opacity: 0.4;
      stroke: #fdba74;
      stroke-width: 1.2;
    }

    > path[data-role='chamada'] {
      stroke: #fdba74;
      stroke-width: 1.2;
    }

    > rect[data-role='rotulo'] {
      fill: #fff;
      fill-opacity: 1;
      stroke: #fdba74;
      stroke-width: 1.2;
    }

    > text[data-role='rotulo-texto'] {
      fill: #9a3412;
      stroke: none;
      font-weight: bold;
    }
  }

  /* ----- realce ao passar o mouse, antes de selecionar ----- */
  a:hover {
    > rect[data-role='area'] {
      fill-opacity: 0.24;
      stroke-width: 1.1;
    }

    > path[data-role='chamada'] {
      stroke-width: 1.1;
    }

    > rect[data-role='rotulo'] {
      fill-opacity: 1;
      stroke-width: 1.1;
    }
  }

  /**
   * O texto da etiqueta não deve receber a regra global
   * "text:hover { fill: #fff }" — ficaria branco sobre fundo branco.
   */
  text[data-role='rotulo-texto']:hover {
    fill: #17263b;
  }
`;

export const ItemArea = styled.div`
  /** não concorre com o desenho: altura definida pelo conteúdo da tabela */
  flex: 0 0 auto;
  display: flex;
  min-height: 0;
  padding: 8px 0 0;
`;

/* ===== Lupa de zoom que acompanha o cursor sobre a imagem =====
   Cartão com duas partes: a área ampliada (MagnifierLens.View) e a faixa
   de informação do item (MagnifierLens.Info).

   Todas as medidas (largura, altura, left/top) são aplicadas inline em
   tempo de execução, a partir das constantes LENS_* do index.js — este
   arquivo não repete nenhum valor, para não haver dois lugares a
   sincronizar.

   pointer-events: none é OBRIGATÓRIO em todo o cartão: se a faixa
   capturasse o mouse, ela engoliria o mouseenter/mouseout dos links do
   desenho e o hover pararia de funcionar. */
export const MagnifierLens = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: none;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;

  /**
   * Explícito de propósito, e não herdado do reset global: as constantes
   * LENS_TOTAL_W/LENS_TOTAL_H do index.js contam a moldura, e a conta da
   * contenção só fecha com border-box.
   */
  box-sizing: border-box;
  background: #fff;
  border: 3px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 34px rgba(6, 14, 26, 0.35),
    0 0 0 1px rgba(23, 38, 59, 0.22);
  pointer-events: none;
  z-index: 30;
  will-change: left, top;
`;

/** área ampliada; a moldura laranja fica aqui, não no cartão inteiro */
MagnifierLens.View = styled.div`
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
  background: #fff;
  box-shadow: inset 0 0 0 2px #ff6d3c;

  img {
    position: absolute;
    top: 0;
    left: 0;
    max-width: none;
    max-height: none;
    user-select: none;
    -webkit-user-drag: none;
  }

  /* mira central para indicar o ponto sob o cursor */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 16px;
    transform: translate(-50%, -50%);
    border: 1px solid rgba(255, 109, 60, 0.85);
    border-radius: 50%;
    box-shadow: 0 0 0 9999px rgba(23, 38, 59, 0.02);
    pointer-events: none;
  }
`;

/**
 * Faixa de informação do item sob o cursor, no rodapé do cartão.
 *
 * Mesma linguagem visual da caixa Info usada com a lupa desligada. A
 * altura é fixa e o texto é truncado: descrição comprida não pode
 * redimensionar o cartão, senão a área ampliada se desloca durante o
 * movimento do mouse.
 */
MagnifierLens.Info = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  overflow: hidden;

  /** a altura recebida inline é a altura FINAL, com o padding dentro */
  box-sizing: border-box;
  padding: 6px 10px;
  background: rgba(23, 38, 59, 0.92);
  color: #fff;

  /* invisível, mas continua ocupando a altura reservada */
  visibility: ${({ visivel }) => (visivel ? 'visible' : 'hidden')};

  > strong {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    font-size: 14px;
    line-height: 1.25;
  }

  > span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    font-size: 12px;
    line-height: 1.2;
    color: rgba(255, 255, 255, 0.78);
  }
`;

/* ===== Botão de liga/desliga da lupa, sobreposto à imagem ===== */
export const LupaToggle = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;

  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.15s ease;

  color: ${({ active }) => (active ? '#fff' : '#2c405a')};
  background: ${({ active }) =>
    active ? '#ff6d3c' : 'rgba(255, 255, 255, 0.92)'};
  border: 1px solid
    ${({ active }) => (active ? '#ff6d3c' : 'rgba(216, 224, 235, 0.95)')};
  box-shadow: ${({ active }) =>
    active
      ? '0 6px 18px rgba(255, 109, 60, 0.35)'
      : '0 4px 12px rgba(23, 38, 59, 0.12)'};

  &:hover {
    transform: translateY(-1px);
    color: ${({ active }) => (active ? '#fff' : '#ff6d3c')};
    box-shadow: ${({ active }) =>
      active
        ? '0 8px 22px rgba(255, 109, 60, 0.45)'
        : '0 6px 16px rgba(23, 38, 59, 0.18)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

/* =====================================================================
   TABELA DE ITENS DA PÁGINA
   ---------------------------------------------------------------------
   thead e cada tr do tbody são tabelas independentes (display: table),
   por isso as larguras de coluna precisam ser declaradas nos DOIS lugares
   com os mesmos valores — caso contrário o cabeçalho desalinha do corpo.

   A quantidade de colunas varia conforme o parâmetro que oculta o código
   ERP, então usa-se nth-child (à esquerda) e nth-last-child (à direita),
   além da prop semErp para a coluna de ERP.
   ===================================================================== */
export const Table = styled.table`
  border-collapse: collapse;

  display: flex;
  flex-flow: column;
  height: 100%;
  width: 100%;

  font-size: 12.5px;
  color: #2c405a;

  > thead {
    flex: 0 0 auto;

    /**
     * Alinhamento com o corpo: o tbody tem 1px de borda em cada lado e,
     * quando rolável, reserva --cat-scrollbar-w para a barra de rolagem
     * (mesmo valor de ::-webkit-scrollbar em styles/global.js).
     * Antes o desconto era 0.9em, que não correspondia à barra real e
     * deixava o cabeçalho deslocado em relação às colunas.
     */
    width: ${({ reduzida }) =>
      reduzida
        ? 'calc(100% - 2px)'
        : 'calc(100% - 2px - var(--cat-scrollbar-w))'};

    > tr {
      > th {
        padding: 7px 10px;
        color: #79869e;
        font-size: 10.5px;
        font-weight: 800;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        text-align: left;
        white-space: nowrap;
      }
    }
  }

  > tbody {
    flex: 1 1 auto;
    display: block;

    /**
     * Tabela reduzida (parâmetro global 34): exibe apenas o item ativo, sem
     * rolagem, para liberar altura em tela para o desenho.
     *
     * Nos demais casos usa-se "scroll" (e não "auto") de propósito: a barra
     * fica sempre reservada, mantendo o cabeçalho alinhado às colunas
     * independentemente da quantidade de itens da página.
     */
    overflow-y: ${({ reduzida }) => (reduzida ? 'hidden' : 'scroll')};
    overflow-x: hidden;
    scrollbar-gutter: stable;

    user-select: text !important;

    /**
     * Altura da área de rolagem vem de --cat-itens-h (styles/global.js),
     * reduzida em tablets / telas de baixa altura. Antes era 20vh fixo.
     */
    height: ${({ reduzida }) => (reduzida ? 'auto' : 'var(--cat-itens-h)')};
    background: #fff;
    border: 1px solid rgba(216, 224, 235, 0.9);
    border-radius: 12px;
    box-shadow: 0 6px 16px rgba(23, 38, 59, 0.05);
  }

  > thead,
  tbody tr {
    display: table;
    table-layout: fixed;
  }

  /* ===== larguras de coluna (cabeçalho e corpo em sincronia) =====
     A coluna de descrição não recebe largura: fica com o espaço restante. */

  /** ITEM */
  > thead > tr > th:nth-child(1),
  > tbody > tr > td:nth-child(1) {
    width: 76px;
  }

  /** PARTNUMBER */
  > thead > tr > th:nth-child(2),
  > tbody > tr > td:nth-child(2) {
    width: 186px;
  }

  /** CÓDIGO ERP (somente quando a coluna é exibida) */
  ${({ semErp }) =>
    !semErp &&
    css`
      > thead > tr > th:nth-child(3),
      > tbody > tr > td:nth-child(3) {
        width: 148px;
      }
    `}

  /** QUANTIDADE */
  > thead > tr > th:nth-last-child(3),
  > tbody > tr > td:nth-last-child(3) {
    width: 74px;
    text-align: right;
    padding-right: 16px;
  }

  /** NOTA */
  > thead > tr > th:nth-last-child(2),
  > tbody > tr > td:nth-last-child(2) {
    width: 62px;
  }

  /** ADICIONAR AO CARRINHO */
  > thead > tr > th:nth-last-child(1),
  > tbody > tr > td:nth-last-child(1) {
    width: 66px;
    text-align: center;
  }

  /** em telas mais estreitas as colunas auxiliares encolhem */
  @media (max-width: 1200px) {
    > thead > tr > th:nth-child(2),
    > tbody > tr > td:nth-child(2) {
      width: 150px;
    }

    ${({ semErp }) =>
      !semErp &&
      css`
        > thead > tr > th:nth-child(3),
        > tbody > tr > td:nth-child(3) {
          width: 120px;
        }
      `}
  }
`;

Table.Row = styled.tr`
  width: 100%;
  transition: background 0.15s ease;

  > td {
    text-align: left;
    border-bottom: 1px solid rgba(216, 224, 235, 0.9);
    padding: 7px 10px;

    /** linhas de altura previsível: texto longo é truncado (title no HTML) */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    button {
      border: 0;
      background: none;
    }

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 3px;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 14px;
        width: 12px;
        flex: 0 0 auto;
      }
    }
  }

  /** zebra discreta facilita percorrer a linha até a coluna desejada */
  &:nth-child(even) > td {
    background: rgba(247, 250, 253, 0.7);
  }

  ${(props) =>
    props.selected &&
    css`
      > td {
        background: rgba(255, 109, 60, 0.14) !important;
        font-weight: bold;
      }

      > td:first-child {
        box-shadow: inset 3px 0 0 #ff6d3c;
      }
    `}

  :hover > td {
    background: rgba(255, 109, 60, 0.1);
    font-weight: bold;
  }
`;

Table.BtnCart = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /** 30px garante alvo de toque confortável em tablet */
  height: 30px;
  width: 30px;
  border-radius: 9px;

  color: ${(props) => (props.inCart ? '#ff6d3c' : '#79869e')};
  background: ${(props) =>
    props.inCart ? 'rgba(255, 109, 60, 0.14)' : 'transparent'};
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  :hover {
    color: ${darken(0.05, '#ff6d3c')};
    background: rgba(255, 109, 60, 0.16);
    transform: scale(1.08);
  }
`;

export const Info = styled.div`
  position: absolute;
  background: rgba(23, 38, 59, 0.92);
  width: 200px;
  min-height: 50px;
  border-radius: 10px;
  padding: 8px 12px;
  color: #fff;
  box-shadow: 0 14px 34px rgba(6, 14, 26, 0.35);
  backdrop-filter: blur(2px);
  top: ${(props) => `${props.posY}px`};
  left: ${(props) => `${props.posX}px`};

  > strong {
    padding: 10px 0;
  }

  /* ::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 110px;
    width: 0;
    border-bottom: 5px solid rgba(44, 64, 90, 0.8);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
  } */
`;

export const NoteContainer = styled(animated.div)`
  position: absolute;
  display: flex;
  flex: 1;
  left: 0;
  bottom: 0;
  margin: 15px;
  color: #27272a;
  background-color: #cbd5e1;
  border-radius: 10px;
  scale: 0.9;

  min-width: 50px;
  max-width: 200px;
  min-height: 30px;
  max-height: 50px;

  overflow: hidden;
  white-space: nowrap;

  transition: all 0.5s;

  :hover {
    cursor: pointer;
    color: #f4f4f5;
    background-color: #64748b;
    transform: scale(1.1);
    transition: all 0.5s;
  }
`;

export const NoteContent = styled(animated.div)`
  position: relative;
  display: flex;
  flex: 1;

  overflow: hidden;
`;

export const Notes = styled.div`
  padding: 10px;
  height: 100%;
  width: 100%;

  & * {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const NotesModal = styled(animated.div)`
  position: fixed;
  top: -100%;
  right: -100%;
  left: -100%;
  bottom: -100%;
  margin: auto;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 30px;
  color: #f4f4f5;
  letter-spacing: 0.7px;
  line-height: 25px;
  text-align: justify;
  background: rgba(23, 38, 59, 0.96);
  border-radius: 14px;
  box-shadow: 0 40px 110px rgba(6, 14, 26, 0.5);
  overflow: auto;
  ${({ sizenote }) => {
    if (sizenote <= 1000) {
      return 'max-width: 40%; max-height: 50%;';
    }
    if (sizenote <= 4000) {
      return 'max-width: 60%; max-height: 70%;';
    }
    return 'max-width: 80%; max-height: 90%;';
  }}
`;

export const NoteHeader = styled.div`
  position: sticky;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  color: #f4f4f5;
  margin-bottom: 15px;

  button {
    background: none;
    border: none;
    color: #f4f4f5;
    font-weight: 800;
    cursor: pointer;
  }
`;
